<template lang="pug">
  .b-node__add(
    v-if="item._allowDropInTree"
    :class="{'_has-connection': hasConnection}"
  )
    .b-node__add-icon._visible(
      ref="tippyTrigger"
      :class="{'_has-connection': hasConnection, '_drag-over': isDragOver}"
      :style="iconStyle"
      @drop="drop"
    )
      font-awesome-icon(
        icon="plus"
        fixed-width
      )

  .b-node__add(
    v-else-if="Array.isArray(newTypes) && newTypes.length > 0"
    :class="{'_has-connection': hasConnection}"
  )
    button.b-node__add-icon(
      type="button"
      ref="tippyTrigger"
      :class="{'_visible': buttonIsVisible, '_has-connection': hasConnection, '_drag-over': hovered}"
      :name="`addMenu-${item.id}`"
      :style="iconStyle"
      @click="showTippy"
    )
      font-awesome-icon(
        icon="plus"
        fixed-width
      )
    tippy(
      :to="`addMenu-${item.id}`"
      arrow
      trigger="manual"
      :interactive="true"
      placement="right"
      theme="light"
      @shown="opened = true"
      @hidden="onHide"
      :key="updatedAt"
    )
      .b-menu(
        :style="menuStyle"
      )
        .b-menu__inner(v-if="!secondLevelType")
          .b-menu__items
            button.b-menu__item(
              v-for="i of newTypes"
              :key="i.value"
              type="button"
              @click="_addNode({type: i.value})"
              :disabled="i.disabled"
              :style="itemStyle"
            ) {{i.key}}
        .b-menu__inner(v-else)
          button.b-menu__toggle-button(
            type="button"
            @click="secondLevelType = false"
          )
            SVGIcon(
              :path="mdiChevronDoubleLeft"
              :style="menuIconStyle"
            )
          .b-menu__items
            button.b-menu__item(
              v-for="(i, index) in secondLevelTypes"
              :key="index"
              type="button"
              @click="_addNode({type: i.value, relatedToItem: i.relatedToItem})"
              :disabled="i.disabled"
              :style="itemStyle"
            ) {{i.key}}
</template>

<script>
import SVGIcon from '@/components/icons/SVGIcon';
import drag from '@/mixims/drag';
import { mdiChevronDoubleLeft } from '@mdi/js';
import { mapFields } from 'vuex-map-fields';
import { mapActions, mapGetters } from 'vuex';
import {
  ELEMENTS_TYPES_LABELS,
  ELEMENTS_TYPES_RELATIONS,
  ELEMENTS_TYPES_RETURN,
  ELEMENTS_TYPES_SECOND_MENU,
} from '@/api/elements';
import { eventBus } from '@/common/eventBus';
import objectValue from '@/utils/objectValue';

export default {
  name: 'NodeAddMenu',
  components: { SVGIcon },
  mixins: [
    drag,
  ],
  props: {
    item: Object,
    hasConnection: Boolean,
    isDragOver: Boolean,
  },
  data() {
    return {
      mdiChevronDoubleLeft,
      opened: false,
      updatedAt: Date.now(),
      secondLevelType: false,
      returnToParent: false,
    };
  },
  mounted() {
    eventBus.$on('showAddMenu', (item) => {
      if (item.from === this.item.id) {
        this.showTippy();
      }
    });
  },
  computed: {
    ...mapFields('visualConnectors', [
      'itemsConnectors',
    ]),
    ...mapFields('nodes', [
      'hoveredItem',
    ]),
    ...mapGetters('nodes', [
      'getItems',
      'getAllowedTypes',
    ]),
    ...mapFields('scale', [
      'layoutScale',
    ]),
    newTypes() {
      const allowed = this.getAllowedTypes(this.item.type);
      let types = [];
      // Filter by type.
      for (const type of allowed) {
        types.push({
          key: ELEMENTS_TYPES_LABELS[type.key],
          value: type.key,
          disabled: type.disabled,
        });
      }
      return types;
    },
    secondLevelTypes() {
      const allowed = this.getAllowedTypes(this.secondLevelType);
      let types = [];
      for (const type of allowed) {
        if (Object.keys(ELEMENTS_TYPES_RELATIONS).includes(type.key)) {
          // Find relations.
          const items = this.getItems({}).filter(i => ELEMENTS_TYPES_RELATIONS[type.key].includes(i.type));
          for (const item of items) {
            types.push({
              key: item._label,
              value: type.key,
              relatedToItem: item,
              disabled: item.id === this.item.id,
            });
          }
        } else {
          types.push({
            key: ELEMENTS_TYPES_LABELS[type.key],
            value: type.key,
            disabled: type.disabled,
          });
        }
      }
      return types;
    },
    position() {
      return objectValue(this.itemsConnectors, `${this.item.id}.connectorParams`);
    },
    hovered() {
      return this.hoveredItem === this.item.id;
    },
    buttonIsVisible() {
      return this.opened || this.hovered;
    },
    menuStyle() {
      return {
        width: `${this.layoutScale * 194}px`,
      };
    },
    iconStyle() {
      return this.hasConnection
        && this.position
        && {
          '--transferX': `${(this.position.bX - this.position.aX) / 2 / this.layoutScale}px`,
          '--transferY': `${(this.position.bY - this.position.aY) / 2 / this.layoutScale}px`,
        };
    },
    itemStyle() {
      return {
        'font-size': `${this.layoutScale * 12}px`,
        padding: `${this.layoutScale * 8}px ${this.layoutScale * 16}px`,
      };
    },
    menuIconStyle() {
      return {
        width: `${this.layoutScale * 20}px`,
        height: `${this.layoutScale * 20}px`,
      };
    },
  },
  methods: {
    onHide() {
      this.opened = false;
      this.secondLevelType = false;
      this.returnToParent = false;
    },
    ...mapActions('nodes', [
      'addNode',
      'swapNodes',
    ]),
    _addNode({
      type,
      relatedToItem,
    }) {
      if (ELEMENTS_TYPES_SECOND_MENU.includes(type)) {
        this.secondLevelType = type;

        if (ELEMENTS_TYPES_RETURN.includes(type)) {
          this.returnToParent = true;
        }

        return true;
      }

      this.addNode({
        from: this.item.id,
        returnToParent: this.returnToParent,
        type,
        relatedToItemId: objectValue(relatedToItem, 'id'),
        isNew: true,
      });
      this.updatedAt = Date.now();
    },
    showTippy() {
      this.$nextTick(() => this.$refs['tippyTrigger'] && this.$refs['tippyTrigger']._tippy.show());
    },
  },
};
</script>
